import { Lock } from "lucide-react";

import { CARD_WALL_RADIUS } from "@/shared/ui/cardWall";
import { cn } from "@/shared/lib/utils";

import {
  isFactRole,
  isImageBlockKey,
  USER_EDIT,
  type HostPageGroup,
} from "../../../model/hostNodes";
import { blockCn, pageCn, previewText, roleCn } from "./hostLabels";
import { NoImagePlaceholder } from "./NoImagePlaceholder";

/**
 * 编辑页左列(host 模式,A.57):页 chips → 当前页的块 → 节点行(角色中文 + 值前 20 字)。
 * ★ 顺序全按 `buildNodeTree`(页 → 块序号 → 字段稳定排序;不信 transplant.nodes 的键序,MySQL JSON 会重排),不在这里重排。
 * ★ 独立图片块(`image0` 这类,不是卡片槽位)块标题下画一行「图片在「各页面图片位」里设置」(1009 C 线:A 线起每个图片位
 *   都能配图,不再一律写「暂无图片」);模板原图发布时照旧清空(A.51)。
 * ★ 事实节点带锁;用户改过的(binding `user:edit`)带一个「已改」小点;有发布阻断的节点标红。
 */
export function HostNodeTree({
  tree,
  page,
  activeToken,
  flagged,
  onSelectPage,
  onSelectNode,
}: {
  tree: HostPageGroup[];
  page: HostPageGroup | null;
  activeToken: string | null;
  /** 发布体检报错的 token(标红)。 */
  flagged: ReadonlySet<string>;
  onSelectPage: (page: string) => void;
  onSelectNode: (token: string) => void;
}) {
  return (
    <section
      aria-label="节点列表"
      className={cn("space-y-3 border border-border bg-card p-3", CARD_WALL_RADIUS)}
    >
      <div role="group" aria-label="页面" className="flex flex-wrap gap-1.5">
        {tree.map((p) => (
          <button
            key={p.page}
            type="button"
            aria-pressed={p.page === page?.page}
            onClick={() => onSelectPage(p.page)}
            className={cn(
              "rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors",
              p.page === page?.page
                ? "border-primary bg-primary/10 text-primary"
                : "border-border text-muted-foreground hover:border-primary/50 hover:text-foreground"
            )}
          >
            {pageCn(p.page)}
            <span className="ml-1 tabular-nums text-2xs opacity-70">
              {p.blocks.reduce((n, b) => n + b.nodes.length, 0)}
            </span>
          </button>
        ))}
      </div>

      {!page ? (
        <p className="py-6 text-center text-xs text-muted-foreground">
          这份草稿里没有可编辑的节点。
        </p>
      ) : (
        <ol className="space-y-2" aria-label={`${pageCn(page.page)} · 区块`}>
          {page.blocks.map((b) => (
            <li key={b.block}>
              <h3 className="px-1 text-2xs font-semibold uppercase tracking-wide text-muted-foreground">
                {blockCn(b.block)}
              </h3>
              {isImageBlockKey(b.block) && <NoImagePlaceholder />}
              <ul className="mt-1 space-y-0.5">
                {b.nodes.map(({ token, node }) => {
                  const active = token === activeToken;
                  const bad = flagged.has(token);
                  return (
                    <li key={token}>
                      <button
                        type="button"
                        data-token={token}
                        aria-current={active ? "true" : undefined}
                        onClick={() => onSelectNode(token)}
                        className={cn(
                          "flex w-full items-center gap-2 rounded-md px-2 py-1 text-left text-xs transition-colors",
                          active ? "bg-primary/10 text-foreground" : "hover:bg-muted/60",
                          bad && "border border-destructive/50 bg-destructive/10"
                        )}
                      >
                        <span className="w-14 shrink-0 text-2xs text-muted-foreground">
                          {roleCn(node.role)}
                        </span>
                        <span className="min-w-0 flex-1 truncate text-foreground">
                          {previewText(node.value)}
                        </span>
                        {isFactRole(node.role) && (
                          <Lock
                            className="h-3 w-3 shrink-0 text-muted-foreground"
                            aria-label="事实字段"
                          />
                        )}
                        {node.binding === USER_EDIT && (
                          <span
                            className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary"
                            aria-label="已改"
                            role="img"
                          />
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
